import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Rating, Comment, Loader, Button, Icon, Image, Modal, Form, TextArea } from 'semantic-ui-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

// Helper to format time relative to now
const timeAgo = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + " years ago";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + " months ago";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + " days ago";
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + " hours ago";
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + " minutes ago";
  return "Just now";
};

const ReportReviewModal = ({ open, onClose, review, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for your report.');
      return;
    }
    setLoading(true);
    try {
      await onSubmit(reason);
      toast.success('Report submitted successfully.');
      onClose(); // Close modal on success
    } catch (error) {
      toast.error(`Failed to submit report: ${error.message}`);
    } finally {
      setLoading(false);
      setReason(''); // Clear reason after submission
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>Report Review</Modal.Header>
      <Modal.Content>
        <p>You are reporting the following review by <strong>{review?.reviewerName}</strong>:</p>
        <Comment>
          <Comment.Content>
            <Comment.Text style={{ fontStyle: 'italic' }}>"{review?.comment}"</Comment.Text>
          </Comment.Content>
        </Comment>
        <Form style={{ marginTop: '1em' }}>
          <TextArea
            placeholder="Please explain why you are reporting this review (e.g., spam, inappropriate content)..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button primary onClick={handleSubmit} loading={loading}>Submit Report</Button>
      </Modal.Actions>
    </Modal>
  );
};

// Helper to get initials for avatar fallback
const getInitials = (name = '') => {
  const names = name.split(' ');
  const first = names[0] ? names[0][0] : '';
  const last = names.length > 1 && names[names.length - 1] ? names[names.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
};

const ReviewList = ({ reviews, loading, onEdit, onDelete }) => {
  const currentUser = useSelector((state) => state.currentUser);
  const [reviewToReport, setReviewToReport] = useState(null);

  const handleOpenReportModal = (review) => {
    setReviewToReport(review);
  };

  const handleCloseReportModal = () => {
    setReviewToReport(null);
  };

  const handleSubmitReport = async (reason) => {
    if (!reviewToReport) return;
    const functions = getFunctions();
    const reportReviewFn = httpsCallable(functions, 'reportReview');
    await reportReviewFn({
      reviewId: reviewToReport.id,
      ownerUID: reviewToReport.ownerUID,
      reason: reason,
    });
  };

  if (loading) {
    return <Loader active inline="centered" content="Loading reviews..." style={{ marginTop: '2em' }} />;
  }

  if (!reviews || reviews.length === 0) {
    return <p style={{ marginTop: '2em' }}>No reviews yet.</p>;
  }

  return (
    <>
      <Comment.Group>
        {reviews.map((review) => (
          <Comment key={review.id} style={{ padding: '1em 0' }}>
            <Comment.Avatar            
              as={() => (
                review.reviewerPhotoURL ? (
                  <Image src={review.reviewerPhotoURL} avatar style={{ maxWidth: '60px', maxHeight: '30px' }} />
                ) : (
                  <div className="avatar-placeholder">{getInitials(review.reviewerName)}</div>
                )
              )}
            />
            <Comment.Content>
              <Comment.Author as="strong">{review.reviewerName}</Comment.Author>
              <Comment.Metadata>
                {/* Show relative time for recent reviews, and date for older ones */}
                <span title={review.createdAt?.seconds ? new Date(review.createdAt.seconds * 1000).toLocaleString() : ''}>{review.createdAt?.seconds ? timeAgo(new Date(review.createdAt.seconds * 1000)) : 'Just now'}</span>
                <Rating icon="star" defaultRating={review.rating} maxRating={5} disabled />
              </Comment.Metadata>
              <Comment.Text>"{review.comment}"</Comment.Text>
              <Comment.Actions>
                {currentUser && currentUser.id === review.reviewerId ? (
                  <>
                    <Button size="mini" basic onClick={() => onEdit(review)}>
                      <Icon name="edit" /> Edit
                    </Button>
                    <Button size="mini" basic color="red" onClick={() => onDelete(review.id, review.ownerUID)}>
                      <Icon name="trash" /> Delete
                    </Button>
                  </>
                ) : currentUser && (
                  <Button size="mini" basic onClick={() => handleOpenReportModal(review)}>
                    <Icon name="flag" /> Report
                  </Button>
                )}
              </Comment.Actions>
            </Comment.Content>
          </Comment>
        ))}
      </Comment.Group>
      <ReportReviewModal
        open={!!reviewToReport}
        onClose={handleCloseReportModal}
        review={reviewToReport}
        onSubmit={handleSubmitReport}
      />
    </>
  );
};

export default ReviewList;
